/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import { reimbursementType, reimbursementUnitOption } from "./ReimbursementInterface";
import { customSelectStyles } from "util/selectStyles";
import reimbursementManager from "services/Reimbursement/reimbursementManager";
import GenericToaster from "components/Toaster";
import Button from "components/Button";
import HeaderButtons from "components/Button/HeaderButtons";
import InlineTable, { fieldCls, TableColumn } from "components/InlineTable";
// Per-expense attachment manager (summary button + preview/manage modal)
import ExpenseAttachments, { AttachedFile } from "./ExpenseAttachments";

// React-select styles for table cells.
// container: width 100% makes the select fill the fixed-width column exactly
// so it never pushes the column wider when a long option is selected.
const tableSelectStyles = {
  ...customSelectStyles,
  container: (base: any) => ({ ...base, width: "100%" }),
  control: (base: any, state: any) => ({
    ...customSelectStyles.control(base, state),
    minHeight: 42,
    minWidth: 0,
    padding: "0 2px",
  }),
  valueContainer: (base: any) => ({
    ...base,
    padding: "0 6px",
    overflow: "hidden",
  }),
  singleValue: (base: any, state: any) => ({
    ...customSelectStyles.singleValue(base, state),
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  }),
  indicatorsContainer: (base: any) => ({
    ...base,
    padding: "0 4px",
    flexShrink: 0,
  }),
};

// ─── Types ───────────────────────────────────────────────────────────────────

interface ExpenseRow {
  id: string;
  type: string;
  amount: string;
  unit: string;
  spentDate: string;
  description: string;
  // Multiple attachments per expense. Each carries its own upload state (S3 mode).
  files: AttachedFile[];
}

interface ExpenseRowErrors {
  amount?: string;
  spentDate?: string;
  description?: string;
  fileError?: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const MAX_FILE_SIZE = 2 * 1024 * 1024;
// Maximum attachments allowed per expense row. Single source of truth for both
// the picker guard and the validation message.
const MAX_FILES_PER_EXPENSE = 3;
// MIME types accepted for reimbursement receipt uploads (images + PDF)
const ALLOWED_FILE_TYPES = ["image/jpeg", "image/png", "image/jpg", "image/jfif", "application/pdf"];
// `accept` attribute value for the receipt file <input>
const FILE_ACCEPT = "image/jpeg,image/jpg,image/png,image/jfif,.jfif,application/pdf";
// Validation error shown when the chosen file's type is not allowed
const INVALID_FILE_TYPE_MESSAGE = "Only JPG, PNG, JFIF, or PDF allowed";
// Helper text describing the accepted formats and size limit
const FILE_HELP_TEXT = "JPG, PNG, JFIF, PDF · Max 2 MB";

// ─── Helpers ─────────────────────────────────────────────────────────────────

const uid = () => Math.random().toString(36).slice(2, 10);
const todayStr = () => new Date().toISOString().split("T")[0];

const newExpense = (): ExpenseRow => ({
  id: uid(),
  type: reimbursementType[0].value,
  amount: "",
  unit: "INR",
  spentDate: "",
  description: "",
  files: [],
});

const EXPENSE_COLUMNS: TableColumn[] = [
  { key: "type", header: "Type", width: 160 },
  { key: "amount", header: "Amount", width: 145 },
  { key: "unit", header: "Unit", width: 130 },
  { key: "spentDate", header: "Spent On", width: 160 },
  { key: "description", header: "Description", width: 195 },
  { key: "attachment", header: "Attachment", width: 210 },
  { key: "remove", header: "", width: 40 },
];

// ─── Main component ───────────────────────────────────────────────────────────

const AddReimbursement = () => {
  const navigate = useNavigate();
  const [expenses, setExpenses] = useState<ExpenseRow[]>([newExpense()]);
  const [expenseErrors, setExpenseErrors] = useState<Record<string, ExpenseRowErrors>>({});
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<{ message: string; severity: "info" | "error" } | null>(null);
  const [storageType, setStorageType] = useState<string>("DRIVE");
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    reimbursementManager.getStorageType()
      .then(({ data }) => setStorageType(data.storage))
      .catch(() => setStorageType("DRIVE"));
  }, []);

  // Inside your component row where "exp" is available:

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;

    // Reset height to force scrollHeight calculation
    el.style.height = "42px";

    // Calculate heights
    const nextHeight = Math.min(el.scrollHeight, 72);
    el.style.height = `${nextHeight}px`;

    // Toggle scrollbar visibility
    el.style.overflowY = el.scrollHeight > 72 ? "auto" : "hidden";
  }, []); // Triggers automatically whenever text changes

  const lastRowRef = useRef<HTMLTableRowElement>(null);

  // Auto-scroll to the newly added row
  useEffect(() => {
    if (expenses.length > 1) {
      lastRowRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [expenses.length]);

  const anyUploading = expenses.some((e) => e.files.some((f) => f.uploading));

  // Determine if the form has been modified from its initial state
  const isFormDirty =
    expenses.length > 1 ||
    expenses[0].amount !== "" ||
    expenses[0].spentDate !== "" ||
    expenses[0].description !== "" ||
    expenses[0].files.length > 0 ||
    expenses[0].type !== reimbursementType[0].value ||
    expenses[0].unit !== "INR";

  // ── Validation ──────────────────────────────────────────────────────────────

  const validateRow = (exp: ExpenseRow): ExpenseRowErrors => {
    const today = todayStr();
    const row: ExpenseRowErrors = {};
    if (!exp.amount || Number(exp.amount) <= 0) row.amount = "Enter a valid amount";
    if (!exp.spentDate) row.spentDate = "Required";
    else if (exp.spentDate > today) row.spentDate = "Cannot be a future date";
    if (!exp.description.trim()) row.description = "Description is required";
    if (exp.files.length === 0) row.fileError = "Attachment is required";
    else if (exp.files.some((f) => f.uploadError)) row.fileError = "Some uploads failed. Re-add them.";
    else if (exp.files.some((f) => f.uploading)) row.fileError = "Please wait for uploads to finish";
    return row;
  };

  const validateExpenses = (): boolean => {
    const errs: Record<string, ExpenseRowErrors> = {};
    expenses.forEach((exp) => {
      const row = validateRow(exp);
      if (Object.keys(row).length) errs[exp.id] = row;
    });
    setExpenseErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const validateLastRow = (): boolean => {
    const last = expenses[expenses.length - 1];
    const row = validateRow(last);
    if (Object.keys(row).length) {
      setExpenseErrors((prev) => ({ ...prev, [last.id]: row }));
      return false;
    }
    return true;
  };

  // ── Expense row handlers ────────────────────────────────────────────────────

  const addExpense = () => {
    if (anyUploading) return;
    if (!validateLastRow()) return;
    setExpenses((prev) => [...prev, newExpense()]);
  };

  const removeExpense = (id: string) => {
    setExpenses((prev) => prev.filter((e) => e.id !== id));
    setExpenseErrors((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const updateExpense = (id: string, field: keyof ExpenseRow, value: any) => {
    setExpenses((prev) => prev.map((e) => (e.id === id ? { ...e, [field]: value } : e)));
    setExpenseErrors((prev) => {
      if (!prev[id]) return prev;
      const row = { ...prev[id] };
      delete (row as any)[field === "description" ? "description" : field === "spentDate" ? "spentDate" : field];
      return { ...prev, [id]: row };
    });
  };

  // Eager S3 upload for a single attachment; updates that file's slot by id.
  const uploadFileToS3 = (expenseId: string, attached: AttachedFile) => {
    reimbursementManager
      .getUploadUrl(attached.file.name)
      .then(async ({ data }) => {
        const putRes = await fetch(data.uploadUrl, { method: "PUT", body: attached.file });
        if (putRes.status !== 200) throw new Error("S3 upload failed");
        setExpenses((prev) =>
          prev.map((ex) =>
            ex.id !== expenseId
              ? ex
              : {
                ...ex,
                files: ex.files.map((f) =>
                  f.id === attached.id ? { ...f, s3Key: data.key, uploading: false, uploadError: "" } : f
                ),
              }
          )
        );
      })
      .catch(() => {
        setExpenses((prev) =>
          prev.map((ex) =>
            ex.id !== expenseId
              ? ex
              : {
                ...ex,
                files: ex.files.map((f) =>
                  f.id === attached.id
                    ? { ...f, uploading: false, uploadError: "Upload failed, please re-add" }
                    : f
                ),
              }
          )
        );
      });
  };

  // Add one or more files to an expense: validates size/type, drops duplicates,
  // enforces the per-expense cap, then (S3 mode) kicks off eager uploads.
  const addFilesToExpense = (expenseId: string, fileList: FileList) => {
    const expense = expenses.find((e) => e.id === expenseId);
    if (!expense) return;

    const existing = expense.files;
    const remaining = MAX_FILES_PER_EXPENSE - existing.length;
    if (remaining <= 0) {
      setToast({ message: `Maximum ${MAX_FILES_PER_EXPENSE} files can be attached to an expense.`, severity: "error" });
      return;
    }

    const accepted: AttachedFile[] = [];
    let limitHit = false;
    let lastError = "";

    for (const file of Array.from(fileList)) {
      if (accepted.length >= remaining) {
        limitHit = true;
        break;
      }
      // Guard against empty filenames.
      if (!file.name || !file.name.trim()) continue;
      if (file.size > MAX_FILE_SIZE) {
        lastError = `${file.name}: Max file size is 2 MB`;
        continue;
      }
      if (!ALLOWED_FILE_TYPES.includes(file.type)) {
        lastError = INVALID_FILE_TYPE_MESSAGE;
        continue;
      }
      // Skip duplicates (same name + size) against existing and already-accepted files.
      const isDuplicate = [...existing, ...accepted].some(
        (a) => a.file.name === file.name && a.file.size === file.size
      );
      if (isDuplicate) continue;

      accepted.push({
        id: uid(),
        file,
        s3Key: null,
        uploading: storageType === "S3",
        uploadError: "",
      });
    }

    if (accepted.length) {
      setExpenses((prev) =>
        prev.map((ex) => (ex.id === expenseId ? { ...ex, files: [...ex.files, ...accepted] } : ex))
      );
      // Clear the "attachment required" error now that files exist.
      setExpenseErrors((prev) => {
        if (!prev[expenseId]) return prev;
        const row = { ...prev[expenseId] };
        delete row.fileError;
        return { ...prev, [expenseId]: row };
      });

      if (storageType === "S3") accepted.forEach((af) => uploadFileToS3(expenseId, af));
    }

    // Surface a single, most-relevant message (limit takes priority over per-file errors).
    if (limitHit) {
      setToast({ message: `Maximum ${MAX_FILES_PER_EXPENSE} files can be attached to an expense.`, severity: "error" });
    } else if (lastError) {
      setToast({ message: lastError, severity: "error" });
    }
  };

  const removeFileFromExpense = (expenseId: string, fileId: string) => {
    setExpenses((prev) =>
      prev.map((ex) => (ex.id === expenseId ? { ...ex, files: ex.files.filter((f) => f.id !== fileId) } : ex))
    );
  };

  // ── Submission ──────────────────────────────────────────────────────────────

  const buildFormData = (expense: ExpenseRow): FormData => {
    const files = expense.files;
    const itemData: Record<string, any> = {
      type: expense.type,
      // Contract keeps a single dataType field — use the first attachment's MIME.
      dataType: files[0]?.file.type ?? null,
      description: expense.description,
      unit: expense.unit,
      submitAmount: Number(expense.amount),
      spentDate: expense.spentDate,
      submitDate: todayStr(),
      // S3: send the list of uploaded keys. DRIVE: null (files sent as parts below).
      data:
        storageType === "S3"
          ? files.map((f) => f.s3Key).filter((k): k is string => Boolean(k))
          : null,
    };

    const formData = new FormData();
    formData.append(
      "reimbursement",
      new Blob([JSON.stringify(itemData)], { type: "application/json" })
    );

    // DRIVE mode: append every file as a separate `data` multipart part.
    if (storageType !== "S3") {
      files.forEach((f) => formData.append("data", f.file));
    }

    return formData;
  };

  const handleSubmit = async () => {
    if (anyUploading) return;
    if (!validateExpenses()) return;
    setSubmitting(true);
    try {
      const results = await Promise.allSettled(
        expenses.map((expense) => {
          const fd = buildFormData(expense);
          return reimbursementManager.addReimbursement(fd);
        })
      );

      const failed = results.filter((r) => r.status === "rejected").length;
      if (failed === 0) {
        setToast({
          message: `${expenses.length} expense${expenses.length > 1 ? "s" : ""} submitted successfully`,
          severity: "info",
        });
        setTimeout(() => navigate("/admin/reimbursement"), 1200);
      } else if (failed < expenses.length) {
        setToast({
          message: `${expenses.length - failed} submitted, ${failed} failed. Please retry the failed items.`,
          severity: "error",
        });
      } else {
        setToast({ message: "All submissions failed. Please try again.", severity: "error" });
      }
    } catch (err: any) {
      setToast({
        message: err?.response?.data?.message || err?.message || "Something went wrong",
        severity: "error",
      });
    }
    setSubmitting(false);
  };

  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-[calc(100vh-70px)] mx-auto mt-2">
      {/* Page header */}
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-4 ">
          <button
            type="button"
            onClick={() => navigate("/admin/reimbursement")}
            className="rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700 transition-colors"
            aria-label="Back to reimbursements"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <div>
            <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Add Reimbursement</h1>
          </div>

        </div>
        <button
          type="button"
          onClick={addExpense}
          disabled={anyUploading}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:opacity-75 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Expense
        </button>
      </div>

      <div className="flex flex-col flex-1 min-h-0 w-full bg-white dark:bg-gray-800 rounded-xl shadow-sm">
        <InlineTable columns={EXPENSE_COLUMNS} minWidth={1040} emptyText="No expenses added" emptySubText="Click Add Expense below to get started">
          {expenses.map((exp, idx) => {
            const errs = expenseErrors[exp.id] ?? {};
            // Attachment error precedence: a failed upload, then row validation.
            const attachmentError = exp.files.some((f) => f.uploadError)
              ? "Some uploads failed. Re-add them."
              : errs.fileError ?? "";

            return (
              <tr key={exp.id} ref={idx === expenses.length - 1 ? lastRowRef : undefined} className="align-bottom pt-2">

                {/* Type */}
                <td className="px-3 py-2.5">
                  <Select
                    styles={tableSelectStyles}
                    options={reimbursementType}
                    value={reimbursementType.find((t) => t.value === exp.type) ?? null}
                    onChange={(opt) => updateExpense(exp.id, "type", opt?.value ?? reimbursementType[0].value)}
                    isSearchable={false}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                  {/* Reserved height to match cells that have an error slot */}
                  <div className="h-4 mt-0.5" />
                </td>

                {/* Amount */}
                <td className="px-3 py-2.5">
                  <input
                    type="number"
                    min={0}
                    value={exp.amount}
                    placeholder="0"
                    onChange={(e) => updateExpense(exp.id, "amount", e.target.value)}
                    onWheel={(e) => (e.target as HTMLInputElement).blur()}
                    className={fieldCls(!!errs.amount)}
                  />
                  <div className="h-4 mt-0.5">
                    <p className={`text-xs font-medium leading-tight ${errs.amount ? "text-error" : "invisible"}`}>
                      {errs.amount ?? "·"}
                    </p>
                  </div>
                </td>

                {/* Unit */}
                <td className="px-3 py-3 overflow-hidden">
                  <Select
                    styles={tableSelectStyles}
                    options={reimbursementUnitOption}
                    value={reimbursementUnitOption.find((u) => u.value === exp.unit) ?? null}
                    onChange={(opt) => updateExpense(exp.id, "unit", opt?.value ?? "INR")}
                    isSearchable={false}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                  {/* Reserved height to match cells that have an error slot */}
                  <div className="h-4 mt-0.5" />
                </td>

                {/* Spent Date */}
                <td className="px-3 py-2.5 overflow-hidden">
                  <input
                    type="date"
                    value={exp.spentDate}
                    max={todayStr()}
                    onChange={(e) => updateExpense(exp.id, "spentDate", e.target.value)}
                    className={`${fieldCls(!!errs.spentDate)} dark:[color-scheme:dark]`}
                  />
                  <div className="h-4 mt-0.5">
                    <p className={`text-xs font-medium leading-tight ${errs.spentDate ? "text-error" : "invisible"}`}>
                      {errs.spentDate ?? "·"}
                    </p>
                  </div>
                </td>

                {/* Description */}
                <td className="px-3 py-2.5 overflow-hidden">
                  <textarea
                    rows={1}
                    value={exp.description}
                    placeholder="e.g. Client travel"
                    onChange={(e) => updateExpense(exp.id, "description", e.target.value)}
                    ref={(el) => {
                      if (!el) return;
                      // Force a clean reset before calculation to ensure accuracy
                      el.style.height = "42px";
                      const nextHeight = Math.min(el.scrollHeight, 72);
                      el.style.height = `${nextHeight}px`;
                      el.style.overflowY = el.scrollHeight > 72 ? "auto" : "hidden";
                    }}
                    className={`${fieldCls(!!errs.description)} resize-none leading-5 block w-full`}
                  />
                  <div className="h-4 mt-0.5">
                    <p className={`text-xs font-medium leading-tight ${errs.description ? "text-error" : "invisible"}`}>
                      {errs.description ?? "·"}
                    </p>
                  </div>
                </td>

                {/* Attachment — compact summary + manage modal (multi-file per expense) */}
                <td className="px-3 py-2.5 overflow-hidden">
                  <ExpenseAttachments
                    files={exp.files}
                    storageType={storageType}
                    maxFiles={MAX_FILES_PER_EXPENSE}
                    accept={FILE_ACCEPT}
                    errorText={attachmentError}
                    helpText={FILE_HELP_TEXT}
                    onAddFiles={(fileList) => addFilesToExpense(exp.id, fileList)}
                    onRemoveFile={(fileId) => removeFileFromExpense(exp.id, fileId)}
                  />
                </td>

                {/* Remove row */}
                <td className="px-2 py-2.5  align-middle">
                  {expenses.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeExpense(exp.id)}
                      title="Remove"
                      className="p-1.5 rounded-lg text-gray-400 hover:text-error hover:bg-error/10 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </InlineTable>

        {/* ── Footer ──────────────────────────────────────────────────────────── */}
        <div className="flex justify-between items-center border-t border-gray-100 dark:border-gray-700 pb-6 pt-4 px-4">
          <Button
            type="button"
            onClick={() => navigate("/admin/reimbursement")}
            className="!bg-slate-100 hover:!bg-slate-200 !text-slate-700 dark:!bg-gray-700 dark:!text-gray-200 dark:hover:!bg-gray-600 !border-0"
          >
            Cancel
          </Button>

          <div className="flex items-center gap-3">
            <HeaderButtons
              type="button"
              onClick={handleSubmit}
              loading={submitting}
              disabled={submitting || anyUploading || !isFormDirty}
              className="bg-secondary !text-white hover:!opacity-90 !border-0 disabled:opacity-50 disabled:cursor-not-allowed"
              icon={
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              }
            >
              Submit Reimbursement
            </HeaderButtons>
          </div>
        </div>

        {toast && (
          <GenericToaster
            open
            message={toast.message}
            severity={toast.severity}
            onClose={() => setToast(null)}
          />
        )}
      </div>
    </div>
  );
};

export default AddReimbursement;